<template>
  <div
    ref="root"
    class="rd rd--stats"
    :data-w="layout"
    :data-density="density"
    :data-bar="barSticky ? 'sticky' : 'flow'"
    :style="rootStyle"
  >
    <div class="rd-main">
      <report-nav :groups="menuGroups" :current="currentKey" :label="$t('routes.statistics')" @select="openScreen">
        <template #tools>
          <button
            type="button"
            class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain"
            :title="`${$t('report_desktop.shortcuts')} (?)`"
            :aria-label="$t('report_desktop.shortcuts')"
            :aria-expanded="String(helpOpen)"
            @click="helpOpen = !helpOpen"
          >
            <v-icon size="18">mdi-keyboard-outline</v-icon>
          </button>
          <button
            type="button"
            class="ui-text-btn rd-nav__legacy"
            :title="$t('report_desktop.old_view')"
            @click="openLegacy"
          >
            <v-icon size="16" color="#c2410c">mdi-history</v-icon>
            <span class="rd-nav__legacy-label">{{ $t("report_desktop.old_view") }}</span>
          </button>
        </template>
      </report-nav>

      <report-filter-bar
        ref="bar"
        :from="from"
        :to="to"
        :presets="presets"
        :show-shifts="false"
        :show-search="false"
        :can-print="false"
        :can-excel="false"
        :loading="loading"
        @period="onPeriod"
        @refresh="reload"
      ></report-filter-bar>

      <div class="rd-page">
        <div class="rd-head">
          <div class="rd-head__title">
            <h1>{{ section ? section.title : $t("routes.statistics") }}</h1>
            <p class="rd-head__sub">{{ periodLong }}</p>
          </div>
          <div class="rd-head__right">
            <label v-if="views.length > 1" class="rd-view">
              <span>{{ $t("view") }}</span>
              <select
                class="rd-select rd-select--sm"
                :value="selectedView"
                :aria-label="$t('view')"
                @change="onView(Number($event.target.value))"
              >
                <option v-for="item in views" :key="item.id" :value="item.id">{{ item.name }}</option>
              </select>
            </label>
            <report-kpis :kpis="kpis"></report-kpis>
          </div>
        </div>

        <div v-if="!view" class="ui-card rd-card rd-loading" aria-busy="true">
          <div v-for="line in 6" :key="line" class="rd-skeleton" :style="{ height: line === 1 ? '36px' : '28px', width: `${100 - ((line * 9) % 31)}%` }"></div>
        </div>
        <report-state
          v-else-if="!view.feed"
          icon="mdi-sitemap-outline"
          :title="$t('statistics_company_multi_restaurant_placeholder')"
          :text="$t('statistics_coming_soon')"
        ></report-state>
        <div v-else class="ui-card rd-card st-card" :aria-busy="String(loading)">
          <div v-if="loading" class="st-loadbar" role="progressbar" :aria-label="$t('loading')"></div>
          <component
            :is="view.feed"
            :key="`${view.id}-${reloadKey}`"
            :date="dateRange"
            :section="currentKey"
            :charts="view.charts"
            @kpis="kpis = $event"
            @loading="loading = $event"
          ></component>
        </div>
      </div>
    </div>

    <report-help-popover :open="helpOpen" :shortcuts="shortcuts" @close="helpOpen = false"></report-help-popover>
  </div>
</template>

<script>
import moment from "moment";
import "@/components/report/desktop/reportDesktop.scss";
import "./statisticsReport.scss";
import ReportNav from "@/components/report/shared/ReportNav.vue";
import ReportHelpPopover from "@/components/report/shared/ReportHelpPopover.vue";
import ReportKpis from "@/components/report/shared/ReportKpis.vue";
import ReportFilterBar from "@/components/report/desktop/ReportFilterBar.vue";
import ReportState from "@/components/report/desktop/ReportState.vue";
import hasReportShell from "@/components/report/shared/hasReportShell";
import { LEGACY_UI_VALUE, QUERY_KEYS } from "@/components/report/desktop/reportQuery";
import hasStatisticsState from "./hasStatisticsState";

const ISO = "YYYY-MM-DD";

/**
 * Statistike na računaru (restoran i firma): gornja navigacija sa grafikonima, traka sa periodom, zaglavlje sa
 * brojkama i grafikon u kartici. Isti izgled i ponašanje kao izveštaji. Podatke učitava stari tab (nasleđen u
 * statsFeeds), grafikoni su postojeći. Stanje (prikaz, grafikon, period) ide u adresu.
 */
export default {
  name: "StatisticsDesktop",
  mixins: [hasReportShell, hasStatisticsState],
  components: { ReportNav, ReportHelpPopover, ReportKpis, ReportFilterBar, ReportState },
  watch: {
    urlQuery: {
      handler(query) {
        this.writeUrl(query);
      },
      deep: true,
    },
    currentKey(key, previous) {
      if (key !== previous) {
        this.kpis = [];
        window.scrollTo(0, 0);
      }
    },
    selectedView() {
      this.kpis = [];
    },
  },
  mounted() {
    this.writeUrl(this.urlQuery);
  },
  methods: {
    onPeriod({ from, to }) {
      if (from !== this.from || to !== this.to) {
        this.setPeriod({ from, to });
      }
    },
    onView(id) {
      this.setView(id);
    },
    openScreen(key) {
      if (!this.menuKeys.includes(key) || key === this.currentKey) {
        return;
      }
      this.$router.push({ query: { ...this.$route.query, [QUERY_KEYS.report]: key } }).catch(() => {});
    },
    shiftDay(step) {
      const move = (date) => moment(date, ISO).add(step, "day").format(ISO);
      this.onPeriod({ from: move(this.from), to: move(this.to) });
    },
    reload() {
      this.reloadKey += 1;
    },
    /** Stara stranica (tabovi): ?ui=stari, dok se novi izgled ne potvrdi. */
    openLegacy() {
      this.$router.replace({ query: { ...this.$route.query, [QUERY_KEYS.legacy]: LEGACY_UI_VALUE } }).catch(() => {});
    },
  },
};
</script>
